import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'contact' | 'apply' | 'partner' | 'book';
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
    align-items: flex-end;
  }

  @media (max-width: 480px) {
    padding: 0;
    align-items: flex-end;
  }
`;

const ModalContent = styled.div<{ isOpen: boolean }>`
  background: linear-gradient(135deg, 
    rgba(248, 250, 252, 0.98) 0%, 
    rgba(237, 242, 247, 0.95) 25%, 
    rgba(226, 232, 240, 0.92) 50%, 
    rgba(203, 213, 224, 0.95) 75%, 
    rgba(248, 250, 252, 0.98) 100%
  );
  border-radius: 25px;
  padding: 3rem;
  width: 100%;
  max-width: 650px;
  position: relative;
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(30px)'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 
    0 25px 50px rgba(66, 153, 225, 0.15),
    0 0 0 1px rgba(66, 153, 225, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(66, 153, 225, 0.2);
  backdrop-filter: blur(30px);
  max-height: 90vh;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(66, 153, 225, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #3182ce, #4299e1);
    border-radius: 3px;
    
    &:hover {
      background: linear-gradient(135deg, #2c5282, #3182ce);
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(66, 153, 225, 0.08) 0%, transparent 50%), 
                radial-gradient(circle at 80% 20%, rgba(144, 205, 244, 0.06) 0%, transparent 50%);
    border-radius: 25px;
    pointer-events: none;
    z-index: -1;
  }

  @media (max-width: 1024px) {
    max-width: 90vw;
    padding: 2.5rem;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 20px 20px 0 0;
    max-height: 85vh;
    transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(100%)'};
    
    &::before {
      border-radius: 20px 20px 0 0;
    }
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 15px 15px 0 0;
    max-height: 90vh;
    
    &::before {
      border-radius: 15px 15px 0 0;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(66, 153, 225, 0.1));
  border: 1px solid rgba(66, 153, 225, 0.2);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #3182ce;
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.15);
  backdrop-filter: blur(10px);
  z-index: 10;

  &:hover {
    background: linear-gradient(135deg, rgba(66, 153, 225, 0.1), rgba(99, 179, 237, 0.15));
    border-color: rgba(66, 153, 225, 0.4);
    transform: scale(1.05) rotate(90deg);
    box-shadow: 0 6px 20px rgba(66, 153, 225, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 1024px) {
    top: 1.5rem;
    right: 1.5rem;
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
  }

  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    top: 0.75rem;
    right: 0.75rem;
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: clamp(1.8rem, 5vw, 2.5rem);
  background: linear-gradient(135deg, #1a365d, #3182ce, #4299e1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  font-weight: 300;
  text-align: center;
  letter-spacing: 1px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #3182ce, transparent);
    border-radius: 1px;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    
    &::after {
      bottom: -0.75rem;
    }
  }
`;

const ModalSubtitle = styled.p`
  color: #4a5568;
  font-size: clamp(0.95rem, 2.5vw, 1.1rem);
  text-align: center;
  margin-bottom: 2.5rem;
  line-height: 1.7;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 2.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;

  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
`;

const Label = styled.label`
  color: #3182ce;
  font-weight: 600;
  font-size: clamp(0.8rem, 2vw, 0.9rem);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 0.25rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: linear-gradient(90deg, #3182ce, #4299e1);
    transition: width 0.3s ease;
  }
  
  ${FormGroup}:focus-within &::after {
    width: 100%;
  }
`;

const Input = styled.input`
  padding: 1.25rem 1rem;
  border: 1px solid rgba(66, 153, 225, 0.2);
  border-radius: 12px;
  font-size: clamp(0.9rem, 2vw, 1rem);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  color: #1a365d;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3182ce;
    box-shadow: 
      0 0 0 3px rgba(66, 153, 225, 0.1),
      0 4px 12px rgba(66, 153, 225, 0.15);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    transform: translateY(-1px);
  }

  &::placeholder {
    color: #a0aec0;
    font-weight: 300;
  }

  &:hover:not(:focus) {
    border-color: rgba(66, 153, 225, 0.4);
    box-shadow: 0 2px 8px rgba(66, 153, 225, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1rem;
  }
`;

const TextArea = styled.textarea`
  padding: 1.25rem 1rem;
  border: 1px solid rgba(66, 153, 225, 0.2);
  border-radius: 12px;
  font-size: clamp(0.9rem, 2vw, 1rem);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 140px;
  resize: vertical;
  font-family: inherit;
  backdrop-filter: blur(10px);
  color: #1a365d;
  line-height: 1.6;
  
  &:focus {
    outline: none;
    border-color: #3182ce;
    box-shadow: 
      0 0 0 3px rgba(66, 153, 225, 0.1),
      0 4px 12px rgba(66, 153, 225, 0.15);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    transform: translateY(-1px);
  }

  &::placeholder {
    color: #a0aec0;
    font-weight: 300;
  }

  &:hover:not(:focus) {
    border-color: rgba(66, 153, 225, 0.4);
    box-shadow: 0 2px 8px rgba(66, 153, 225, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    min-height: 120px;
    font-size: 1rem;
  }
`;

const Select = styled.select`
  padding: 1.25rem 1rem;
  border: 1px solid rgba(66, 153, 225, 0.2);
  border-radius: 12px;
  font-size: clamp(0.9rem, 2vw, 1rem);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8));
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  backdrop-filter: blur(10px);
  color: #1a365d;
  font-family: inherit;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%233182ce' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 1rem center;
  background-repeat: no-repeat;
  background-size: 1rem;
  padding-right: 3rem;
  
  &:focus {
    outline: none;
    border-color: #3182ce;
    box-shadow: 
      0 0 0 3px rgba(66, 153, 225, 0.1),
      0 4px 12px rgba(66, 153, 225, 0.15);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: rgba(66, 153, 225, 0.4);
    box-shadow: 0 2px 8px rgba(66, 153, 225, 0.1);
  }

  option {
    background: #ffffff;
    color: #1a365d;
    padding: 0.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    padding-right: 2.5rem;
    font-size: 1rem;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #3182ce, #4299e1, #63b3ed);
  color: white;
  border: none;
  padding: 1.25rem 2rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: clamp(0.95rem, 2.5vw, 1.1rem);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 1.5rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(66, 153, 225, 0.3);
  border: 1px solid rgba(66, 153, 225, 0.5);
  text-transform: uppercase;
  letter-spacing: 1px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(66, 153, 225, 0.4);
    background: linear-gradient(135deg, #2c5282, #3182ce, #4299e1);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    background: linear-gradient(135deg, #a0aec0, #cbd5e0);
    box-shadow: 0 2px 8px rgba(160, 174, 192, 0.2);
    
    &::before {
      display: none;
    }
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    border-radius: 15px;
    margin-top: 1rem;
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, 
    rgba(34, 197, 94, 0.12), 
    rgba(22, 163, 74, 0.08), 
    rgba(34, 197, 94, 0.05)
  );
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #059669;
  padding: 2.5rem 2rem;
  border-radius: 20px;
  text-align: center;
  font-weight: 500;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 25px rgba(34, 197, 94, 0.15);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, #22c55e, transparent);
    border-radius: 20px 20px 0 0;
  }

  h3 {
    font-size: clamp(1.3rem, 4vw, 1.6rem);
    font-weight: 600;
    margin-bottom: 1rem;
    color: #16a34a;
    letter-spacing: 0.5px;
  }

  p {
    font-size: clamp(0.95rem, 2.5vw, 1.1rem);
    line-height: 1.6;
    color: #059669;
    margin: 0;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 15px;
    
    &::before {
      border-radius: 15px 15px 0 0;
    }
  }
`;

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, type }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    position: '',
    experience: '',
    package: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        position: '',
        experience: '',
        package: ''
      });
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);

    // Auto close after 3 seconds
    setTimeout(() => {
      onClose();
    }, 3000);
  };

  const getModalContent = () => {
    switch (type) {
      case 'apply':
        return {
          title: t('apply_position'),
          subtitle: t('apply_subtitle'),
          showPosition: false,
          showExperience: true
        };
      case 'partner':
        return {
          title: t('become_partner'),
          subtitle: t('partner_subtitle'),
          showPosition: false,
          showExperience: false
        };
      case 'book':
        return {
          title: t('book_adventure'),
          subtitle: t('book_subtitle'),
          showPackage: true,
          showPosition: false,
          showExperience: false
        };
      default:
        return {
          title: t('contact_us'),
          subtitle: t('contact_subtitle'),
          showPosition: false,
          showExperience: false
        };
    }
  };

  const modalContent = getModalContent();

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent isOpen={isOpen}>
        <CloseButton onClick={onClose} aria-label="Close">
          ✕
        </CloseButton>
        
        {isSubmitted ? (
          <SuccessMessage>
            <h3>✅ {t('message_sent')}</h3>
            <p>{t('message_sent_description')}</p>
          </SuccessMessage>
        ) : (
          <>
            <ModalTitle>{modalContent.title}</ModalTitle>
            <ModalSubtitle>{modalContent.subtitle}</ModalSubtitle>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">{t('full_name')} *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('enter_name')}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">{t('email')} *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('enter_email')}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">{t('phone')}</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('enter_phone')}
                />
              </FormGroup>

              {modalContent.showPosition && (
                <FormGroup>
                  <Label htmlFor="position">{t('desired_position')} *</Label>
                  <Select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">{t('select_position')}</option>
                    <option value="captain">{t('captain')}</option>
                    <option value="first_mate">{t('first_mate')}</option>
                    <option value="navigator">{t('navigator')}</option>
                    <option value="engineer">{t('engineer')}</option>
                    <option value="cook">{t('cook')}</option>
                    <option value="deckhand">{t('deckhand')}</option>
                  </Select>
                </FormGroup>
              )}

              {modalContent.showExperience && (
                <FormGroup>
                  <Label htmlFor="experience">{t('sailing_experience')} *</Label>
                  <TextArea
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    placeholder={t('describe_experience')}
                    required
                  />
                </FormGroup>
              )}

              {modalContent.showPackage && (
                <FormGroup>
                  <Label htmlFor="package">{t('tour_package')} *</Label>
                  <Select
                    id="package"
                    name="package"
                    value={formData.package}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">{t('select_package')}</option>
                    <option value="arc_rally">{t('arc_rally_experience')}</option>
                    <option value="pacific">{t('pacific_adventure')}</option>
                    <option value="arctic">{t('arctic_expedition')}</option>
                    <option value="full">{t('full_circumnavigation')}</option>
                  </Select>
                </FormGroup>
              )}

              <FormGroup>
                <Label htmlFor="message">{t('message')} *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t('enter_message')}
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('sending') : t('send_message')}
              </SubmitButton>
            </Form>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ContactModal; 
